import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaEdit, FaUser, FaPhone, FaEnvelope, FaCalendar, FaBriefcase, FaMapMarkerAlt, FaUserShield, FaMoneyCheckAlt, FaIdCard } from "react-icons/fa";
import { MdDelete, MdSave, MdCancel } from "react-icons/md";
import EmployeeService from "../services/EmployeeService";
import { AuthContext } from "../context/AuthContext";

const EmployeeProfile = () => {
  const employeeId = useParams().employeeId ;
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);
  
  const [formData, setFormData] = useState({
    employeeId: '',
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      maritalStatus: 'Single',
      nationality: 'Indian',
      aadharNumber: '',
      panNumber: ''
    },
    address: {
      current: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      permanent: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      }
    },
    emergencyContact: {
      name: '',
      phone: '',
      relation: '',
      address: ''
    },
    department: '',
    position: '',
    joinedDate: '',
    salary: {
      bankDetails: {
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: '',
        accountHolderName: ''
      }
    }
  });

  const isAdmin = ["ADMIN", "CEO", "VAIBHAV"].includes(user?.role);
  const canEdit = isAdmin || (!employeeId && user?.role === "EMP-HR");

  const departmentOptions = {
    IT: ['Developer', 'System Administrator', 'IT Support', 'DevOps Engineer', 'QA Engineer'],
    HR: ['HR Manager', 'HR Assistant', 'Recruiter', 'Training Coordinator'],
    Finance: ['Accountant', 'Financial Analyst', 'Finance Manager', 'Payroll Specialist'],
    Marketing: ['Marketing Executive', 'SEO Specialist', 'Content Creator', 'Brand Manager'],
    Operations: ['Operations Manager', 'Project Coordinator', 'Business Analyst'],
    Sales: ['Sales Executive', 'Sales Manager', 'Account Manager']
  };

  useEffect(() => {
    fetchEmployeeData();
  }, [employeeId, user]);

  const fetchEmployeeData = async () => {
    try {
      let data;
      
      if (employeeId && isAdmin) {
        data = await EmployeeService.getEmployeeById(employeeId);
      } else {
        data = await EmployeeService.getEmployeeById(user.employee_id);
      }
      
      if (data) {
        setEmployeeData(data);
        
        // Parse the data to match our form structure
        setFormData({
          employeeId: data.employeeId || '',
          personalInfo: {
            firstName: data.personalInfo?.firstName || '',
            lastName: data.personalInfo?.lastName || '',
            email: data.personalInfo?.email || data.user?.email || '',
            phone: data.personalInfo?.phone || data.user?.phone || '',
            dateOfBirth: data.personalInfo?.dateOfBirth ? 
              new Date(data.personalInfo.dateOfBirth).toISOString().split('T')[0] : '',
            gender: data.personalInfo?.gender || '',
            maritalStatus: data.personalInfo?.maritalStatus || 'Single',
            nationality: data.personalInfo?.nationality || 'Indian',
            aadharNumber: data.personalInfo?.aadharNumber || '',
            panNumber: data.personalInfo?.panNumber || ''
          },
          address: {
            current: data.address?.current || {
              street: '',
              city: '',
              state: '',
              pincode: '',
              country: 'India'
            },
            permanent: data.address?.permanent || {
              street: '',
              city: '',
              state: '',
              pincode: '',
              country: 'India'
            }
          },
          emergencyContact: data.emergencyContact || {
            name: '',
            phone: '',
            relation: '',
            address: ''
          },
          department: data.department || '',
          position: data.position || '',
          joinedDate: data.joinedDate ? 
            new Date(data.joinedDate).toISOString().split('T')[0] : '',
          salary: {
            bankDetails: data.salary?.bankDetails || {
              accountNumber: '',
              ifscCode: '',
              bankName: '',
              branchName: '',
              accountHolderName: ''
            }
          }
        });
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching employee data:", err);
      setError("Failed to load employee profile.");
      setLoading(false);
    }
  };

  const handleNestedChange = (section, field, value, subsection = null) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (subsection) {
        newData[section][subsection][field] = value;
      } else {
        newData[section][field] = value;
      }
      return newData;
    });
  };

  const handleDirectChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressCopy = (checked) => {
    setSameAsCurrentAddress(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          permanent: { ...prev.address.current }
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updatedData;
      
      if (employeeId && isAdmin) {
        updatedData = await EmployeeService.updateEmployee(employeeData._id, formData);
      } else {
        updatedData = await EmployeeService.updateProfile(formData);
      }
      
      setEmployeeData(updatedData);
      setIsEditing(false);
      setLoading(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile.");
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this employee?")) {
      return;
    }
    
    try {
      await EmployeeService.deleteEmployee(employeeData._id);
      navigate('/dashboard/employee-list');
    } catch (err) {
      console.error("Error deleting employee:", err);
      setError("Failed to delete employee.");
    }
  };

  const toggleEditMode = () => {
    if (isEditing) {
      // Reset form data when canceling
      fetchEmployeeData();
    }
    setIsEditing(!isEditing);
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: <FaUser /> },
    { id: 'contact', label: 'Contact', icon: <FaPhone /> },
    { id: 'address', label: 'Address', icon: <FaMapMarkerAlt /> },
    { id: 'emergency', label: 'Emergency', icon: <FaUserShield /> },
    { id: 'employment', label: 'Employment', icon: <FaBriefcase /> },
    { id: 'bank', label: 'Bank Details', icon: <FaMoneyCheckAlt /> },
    { id: 'documents', label: 'Documents', icon: <FaIdCard /> }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md m-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  if (!employeeData) {
    return (
      <div className="bg-yellow-100 text-yellow-700 p-4 rounded-md m-4" role="alert">
        <p className="font-bold">No Employee Profile Found</p>
        <p>The requested employee profile does not exist.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {formData.personalInfo.firstName.charAt(0)}{formData.personalInfo.lastName.charAt(0)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {formData.personalInfo.firstName} {formData.personalInfo.lastName}
              </h1>
              <p className="text-gray-600">{formData.position} - {formData.department}</p>
              <p className="text-sm text-gray-500">Employee ID: {formData.employeeId}</p>
            </div>
          </div>
          
          {canEdit  && (
            <div className="flex space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSubmit}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                  >
                    <MdSave /> <span>Save</span>
                  </button>
                  <button
                    onClick={toggleEditMode}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                  >
                    <MdCancel /> <span>Cancel</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={toggleEditMode}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                  >
                    <FaEdit /> <span>Edit</span>
                  </button>
                  {isAdmin && employeeId && (
                    <button
                      onClick={handleDelete}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.personalInfo.firstName}
                    onChange={(e) => handleNestedChange('personalInfo', 'firstName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.personalInfo.lastName}
                    onChange={(e) => handleNestedChange('personalInfo', 'lastName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={formData.personalInfo.dateOfBirth}
                    onChange={(e) => handleNestedChange('personalInfo', 'dateOfBirth', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">
                    {formData.personalInfo.dateOfBirth ? 
                      new Date(formData.personalInfo.dateOfBirth).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                {isEditing ? (
                  <select
                    value={formData.personalInfo.gender}
                    onChange={(e) => handleNestedChange('personalInfo', 'gender', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.gender || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marital Status
                </label>
                {isEditing ? (
                  <select
                    value={formData.personalInfo.maritalStatus}
                    onChange={(e) => handleNestedChange('personalInfo', 'maritalStatus', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.maritalStatus}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nationality
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.personalInfo.nationality}
                    onChange={(e) => handleNestedChange('personalInfo', 'nationality', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.nationality}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aadhar Number
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.personalInfo.aadharNumber}
                    onChange={(e) => handleNestedChange('personalInfo', 'aadharNumber', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="12"
                  />
                ) : (
                  <p className="text-gray-900 py-2">
                    {formData.personalInfo.aadharNumber ? 
                      `XXXX-XXXX-${formData.personalInfo.aadharNumber.slice(-4)}` : '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PAN Number
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.personalInfo.panNumber}
                    onChange={(e) => handleNestedChange('personalInfo', 'panNumber', e.target.value.toUpperCase())}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="10"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.panNumber || '-'}</p>
                )}
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.personalInfo.email}
                    onChange={(e) => handleNestedChange('personalInfo', 'email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaPhone className="inline mr-2" />
                  Phone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.personalInfo.phone}
                    onChange={(e) => handleNestedChange('personalInfo', 'phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.personalInfo.phone}</p>
                )}
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Current Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.current.street}
                        onChange={(e) => handleNestedChange('address', 'street', e.target.value, 'current')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.current.street || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.current.city}
                        onChange={(e) => handleNestedChange('address', 'city', e.target.value, 'current')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.current.city || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.current.state}
                        onChange={(e) => handleNestedChange('address', 'state', e.target.value, 'current')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.current.state || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.current.pincode}
                        onChange={(e) => handleNestedChange('address', 'pincode', e.target.value, 'current')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.current.pincode || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.current.country}
                        onChange={(e) => handleNestedChange('address', 'country', e.target.value, 'current')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.current.country}</p>
                    )}
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="sameAddress"
                    checked={sameAsCurrentAddress}
                    onChange={(e) => handleAddressCopy(e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="sameAddress" className="text-sm text-gray-700">
                    Permanent address same as current address
                  </label>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Permanent Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Street</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.permanent.street}
                        onChange={(e) => handleNestedChange('address', 'street', e.target.value, 'permanent')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={sameAsCurrentAddress}
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.permanent.street || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.permanent.city}
                        onChange={(e) => handleNestedChange('address', 'city', e.target.value, 'permanent')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={sameAsCurrentAddress}
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.permanent.city || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.permanent.state}
                        onChange={(e) => handleNestedChange('address', 'state', e.target.value, 'permanent')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={sameAsCurrentAddress}
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.permanent.state || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.permanent.pincode}
                        onChange={(e) => handleNestedChange('address', 'pincode', e.target.value, 'permanent')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={sameAsCurrentAddress}
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.permanent.pincode || '-'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.address.permanent.country}
                        onChange={(e) => handleNestedChange('address', 'country', e.target.value, 'permanent')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={sameAsCurrentAddress}
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{formData.address.permanent.country}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Emergency Contact Tab */}
          {activeTab === 'emergency' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.emergencyContact.name}
                    onChange={(e) => handleNestedChange('emergencyContact', 'name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.emergencyContact.name || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.emergencyContact.phone}
                    onChange={(e) => handleNestedChange('emergencyContact', 'phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.emergencyContact.phone || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.emergencyContact.relation}
                    onChange={(e) => handleNestedChange('emergencyContact', 'relation', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.emergencyContact.relation || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.emergencyContact.address}
                    onChange={(e) => handleNestedChange('emergencyContact', 'address', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.emergencyContact.address || '-'}</p>
                )}
              </div>
            </div>
          )}

          {/* Employment Tab */}
          {activeTab === 'employment' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee ID
                </label>
                {isEditing && isAdmin ? (
                  <input
                    type="text"
                    value={formData.employeeId}
                    onChange={(e) => handleDirectChange('employeeId', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.employeeId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                {isEditing ? (
                  <select
                    value={formData.department}
                    onChange={(e) => {
                      handleDirectChange('department', e.target.value);
                      handleDirectChange('position', '');
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Department</option>
                    {Object.keys(departmentOptions).map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">{formData.department || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position
                </label>
                {isEditing ? (
                  <select
                    value={formData.position}
                    onChange={(e) => handleDirectChange('position', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!formData.department}
                  >
                    <option value="">Select Position</option>
                    {formData.department && departmentOptions[formData.department]?.map(pos => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">{formData.position || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaCalendar className="inline mr-2" />
                  Joining Date
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    value={formData.joinedDate}
                    onChange={(e) => handleDirectChange('joinedDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">
                    {formData.joinedDate ? 
                      new Date(formData.joinedDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Role
                </label>
                <p className="text-gray-900 py-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    employeeData?.user?.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                    employeeData?.user?.role === 'CEO' ? 'bg-red-100 text-red-800' :
                    employeeData?.user?.role === 'EMP' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {employeeData?.user?.role || 'N/A'}
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <p className="text-gray-900 py-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    employeeData?.user?.status === 'active' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {employeeData?.user?.status || 'N/A'}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bank' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.salary.bankDetails.accountNumber}
                    onChange={(e) => handleNestedChange('salary', 'accountNumber', e.target.value, 'bankDetails')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">
                    {formData.salary.bankDetails.accountNumber ? 
                      `XXXX-XXXX-${formData.salary.bankDetails.accountNumber.slice(-4)}` : '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IFSC Code
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.salary.bankDetails.ifscCode}
                    onChange={(e) => handleNestedChange('salary', 'ifscCode', e.target.value.toUpperCase(), 'bankDetails')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="11"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.salary.bankDetails.ifscCode || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.salary.bankDetails.bankName}
                    onChange={(e) => handleNestedChange('salary', 'bankName', e.target.value, 'bankDetails')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.salary.bankDetails.bankName || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.salary.bankDetails.branchName}
                    onChange={(e) => handleNestedChange('salary', 'branchName', e.target.value, 'bankDetails')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.salary.bankDetails.branchName || '-'}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Holder Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.salary.bankDetails.accountHolderName}
                    onChange={(e) => handleNestedChange('salary', 'accountHolderName', e.target.value, 'bankDetails')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.salary.bankDetails.accountHolderName || '-'}</p>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              {employeeData?.documents && employeeData.documents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employeeData.documents.map((doc, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <FaIdCard className="text-2xl text-blue-500" />
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          doc.type === 'Resume' ? 'bg-blue-100 text-blue-800' :
                          doc.type === 'Offer Letter' ? 'bg-green-100 text-green-800' :
                          doc.type === 'ID Proof' ? 'bg-purple-100 text-purple-800' :
                          doc.type === 'Address Proof' ? 'bg-yellow-100 text-yellow-800' :
                          doc.type === 'Educational Certificate' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {doc.type}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-800 mb-1">{doc.name}</h4>
                      <p className="text-sm text-gray-500">
                        Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                      </p>
                      {doc.fileUrl && (
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 text-sm mt-2 inline-block"
                        >
                          View Document →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaIdCard className="text-4xl mx-auto mb-3 text-gray-300" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Employment Duration</h3>
          <p className="text-2xl font-bold text-blue-600">
            {formData.joinedDate ? 
              Math.floor((new Date() - new Date(formData.joinedDate)) / (1000 * 60 * 60 * 24 * 365)) : 0} years
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Since {formData.joinedDate ? new Date(formData.joinedDate).getFullYear() : 'N/A'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Department</h3>
          <p className="text-xl font-bold text-green-600">{formData.department || 'Not Assigned'}</p>
          <p className="text-sm text-gray-500 mt-1">{formData.position || 'No position'}</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Contact Info</h3>
          <p className="text-sm text-gray-600">
            <FaEnvelope className="inline mr-2" />
            {formData.personalInfo.email}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            <FaPhone className="inline mr-2" />
            {formData.personalInfo.phone}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;